<script setup lang="ts">
/**
 * 空画板引导：中央卡片 + 拖拽落区（画板虚线描边由 .canvas-host.is-blank 打在 .sve-viewport 上）。
 *
 * 只解决一件事 —— 一进来画布全空，而「打开 SVG」在最容易漏看的右下角。
 * 显示条件在 CanvasStage（有文档 + 零元素 + 非宿主嵌入）；容器 pointer-events:none，
 * 只有卡片本体可点，绝不挡住画布交互。
 * 拖拽文件进窗口时卡片让位给一句「松开即可打开 .svg」：那一刻用户要的是完成，不是三选一。
 */
import OpenFileButton from '@/components/OpenFileButton.vue';
import { useEditor } from '@/composables/useEditor';
import { DEMO_SVG } from '@/demo';
import { ui } from '@/stores/editor';

const editor = useEditor();

/** 载入内置示例：比任何说明都快的「上手第一眼」（示例内容见 demo.ts） */
const loadDemo = (): void => {
  editor.openText(DEMO_SVG, 'demo.svg');
};
</script>

<template>
  <div class="guide">
    <p v-if="ui.dragOver" class="guide-drop" data-field="guide-drop">松开即可打开 .svg</p>

    <section v-else class="guide-card" data-field="guide-card" aria-label="开始使用">
      <button class="guide-x" title="关闭" aria-label="关闭" @click="ui.blankDismissed = true">×</button>

      <div class="guide-ic">
        <svg
          viewBox="0 0 24 24"
          width="21"
          height="21"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="3" />
          <path d="M3 15.5 8.5 10l5 5 3-3 4.5 4.5" />
          <circle cx="8.8" cy="8.2" r="1.6" />
        </svg>
      </div>

      <h2>开始你的第一张图</h2>
      <p class="guide-desc">画板还是空的。打开一份 SVG 继续编辑，或者先看看示例长什么样。</p>

      <div class="guide-acts">
        <OpenFileButton kind="svg" label="打开 SVG 文件" class="guide-btn is-primary" />
        <button class="guide-btn" @click="loadDemo">载入示例</button>
      </div>

      <p class="guide-foot">也可以把 <b>.svg</b> 文件直接拖进窗口 · <b>Ctrl+Shift+V</b> 从剪贴板打开</p>
    </section>
  </div>
</template>
